import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { getTransfers, createTransfer, shipTransfer, receiveTransfer, cancelTransfer, getWarehouses, getVariants, startTransferPick, createTransferInboundReceipt, listSerialPallets, getSerialPallet, transferSerialPallet, lookupSerial } from '../lib/api';
import { toast } from 'sonner';
import { Truck, Plus, ArrowsLeftRight, Package, X, CheckCircle, PaperPlaneTilt, Receipt, HandGrabbing, Tray, Barcode } from '@phosphor-icons/react';
import HelpAnchor from '../components/HelpAnchor';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import FilterBar from '../components/FilterBar';
import BulkActionsBar from '../components/BulkActionsBar';
import useBulkSelection from '../lib/useBulkSelection';
import { matchesCreatedWindow, CREATED_WINDOW_OPTIONS } from '../lib/dateWindow';
import useUrlFilterState, { useUrlFilterClear } from '../lib/useUrlFilterState';
import TransferInvoicingDialog from '../components/TransferInvoicingDialog';
import BatchSettleDialog from '../components/BatchSettleDialog';

const statusColors = {
  pending: 'bg-amber-100 text-amber-800',
  picking: 'bg-brand/10 text-brand',
  in_transit: 'bg-blue-100 text-blue-800',
  received: 'bg-green-100 text-green-800',
  cancelled: 'bg-muted text-slate-800',
};

export default function Transfers() {
  const [transfers, setTransfers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useUrlFilterState('status', '');
  const [searchTerm, setSearchTerm] = useUrlFilterState('q', '');
  const [fromWhFilter, setFromWhFilter] = useUrlFilterState('from', '');
  const [toWhFilter, setToWhFilter] = useUrlFilterState('to', '');
  const [dateWindow, setDateWindow] = useUrlFilterState('date', '');
  const clearUrlFilters = useUrlFilterClear(['q', 'status', 'from', 'to', 'date']);
  const transferSel = useBulkSelection();
  const [newTransfer, setNewTransfer] = useState({
    from_warehouse_id: '', to_warehouse_id: '', notes: '',
    items: [{ variant_id: '', quantity: 1 }]
  });
  const [invoicingFor, setInvoicingFor] = useState(null);
  const [batchSettleFor, setBatchSettleFor] = useState(null);
  // iter206: pallet-loaded state (iter207: now supports multiple pallets)
  const [loadedPalletIds, setLoadedPalletIds] = useState([]);
  const [showPalletPicker, setShowPalletPicker] = useState(false);
  const [availablePallets, setAvailablePallets] = useState([]);
  const [palletsLoading, setPalletsLoading] = useState(false);
  const [pickerSelected, setPickerSelected] = useState(new Set());
  const [scanValue, setScanValue] = useState('');
  const [scanHint, setScanHint] = useState('');
  const [scanBusy, setScanBusy] = useState(false);
  const scanInputRef = React.useRef(null);
  const loadSelectedBtnRef = React.useRef(null);

  const fetchData = async () => {
    try {
      const [tRes, wRes, vRes] = await Promise.all([
        getTransfers(filter ? { status: filter } : {}),
        getWarehouses(),
        getVariants()
      ]);
      setTransfers(tRes.data);
      setWarehouses(wRes.data);
      setVariants(vRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [filter]);

  // Compose: server-filtered transfers narrowed further by search + warehouse pair
  const filteredTransfers = React.useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return transfers.filter((t) => {
      if (term && !String(t.transfer_number || '').toLowerCase().includes(term)) return false;
      if (fromWhFilter && t.from_warehouse_id !== fromWhFilter) return false;
      if (toWhFilter && t.to_warehouse_id !== toWhFilter) return false;
      if (!matchesCreatedWindow(t.created_at, dateWindow)) return false;
      return true;
    });
  }, [transfers, searchTerm, fromWhFilter, toWhFilter, dateWindow]);

  const addItem = () => setNewTransfer({ ...newTransfer, items: [...newTransfer.items, { variant_id: '', quantity: 1 }] });
  const updateItem = (idx, field, value) => {
    const items = [...newTransfer.items];
    items[idx][field] = value;
    setNewTransfer({ ...newTransfer, items });
  };
  const removeItem = (idx) => setNewTransfer({ ...newTransfer, items: newTransfer.items.filter((_, i) => i !== idx) });

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const created = await createTransfer(newTransfer);
      // iter207: link every loaded pallet to the created transfer so their
      // serials move to the destination warehouse in one call. Failures
      // are collected but don't block the transfer — the pallet linkage
      // can be re-tried later.
      const failedPallets = [];
      for (const pid of loadedPalletIds) {
        try {
          await transferSerialPallet(pid, {
            to_warehouse_id: newTransfer.to_warehouse_id,
            transfer_id: created?.data?.id,
            note: `Loaded on transfer ${created?.data?.transfer_number || ''}`,
          });
        } catch (err) {
          failedPallets.push(pid);
        }
      }
      if (loadedPalletIds.length && !failedPallets.length) {
        toast.success(`${loadedPalletIds.length} pallet${loadedPalletIds.length === 1 ? '' : 's'} linked — all serials moved to destination.`);
      } else if (failedPallets.length) {
        toast.warning(`${failedPallets.length}/${loadedPalletIds.length} pallet link(s) failed — the transfer was still created.`);
      }
      setShowCreate(false);
      setNewTransfer({ from_warehouse_id: '', to_warehouse_id: '', notes: '', items: [{ variant_id: '', quantity: 1 }] });
      setLoadedPalletIds([]);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Create failed');
    }
  };

  // iter206: open the pallet picker — fetches in-stock pallets in the
  // current source warehouse and lets the operator load one into the
  // transfer's items array with a single click.
  const openPalletPicker = async () => {
    setShowPalletPicker(true);
    setPalletsLoading(true);
    setPickerSelected(new Set());
    try {
      const params = { status: 'in_stock', limit: 200 };
      if (newTransfer.from_warehouse_id) params.warehouse_id = newTransfer.from_warehouse_id;
      const r = await listSerialPallets(params);
      setAvailablePallets(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load pallets');
    } finally {
      setPalletsLoading(false);
    }
  };

  // iter208: scan-to-load — accepts either a raw PAL-XXXXXXXX code (from a
  // pallet-level barcode) OR the serial-sticker QR payload STK:<company>:<sn>
  // (any sticker on the pallet identifies the parent). Ticks the matched
  // pallet and moves focus to "Load selected".
  const handleScan = async (raw) => {
    const val = (raw || '').trim();
    if (!val) return;
    setScanBusy(true); setScanHint('');
    try {
      let target = null;
      // Case A: pallet code
      if (val.toUpperCase().startsWith('PAL-')) {
        target = availablePallets.find((p) => (p.pallet_code || '').toUpperCase() === val.toUpperCase());
      }
      // Case B: serial sticker QR
      else if (val.toUpperCase().startsWith('STK:')) {
        const sn = val.split(':').slice(2).join(':');
        try {
          const r = await lookupSerial(sn);
          const pid = r.data?.serial?.pallet_id;
          if (pid) target = availablePallets.find((p) => p.id === pid);
          else { setScanHint(`Serial '${sn}' is not on any pallet`); return; }
        } catch (e) {
          setScanHint(`Serial '${sn}' not found`);
          return;
        }
      }
      // Case C: bare serial number (typed directly)
      else {
        try {
          const r = await lookupSerial(val);
          const pid = r.data?.serial?.pallet_id;
          if (pid) target = availablePallets.find((p) => p.id === pid);
          else { setScanHint(`Serial '${val}' is not on any pallet`); return; }
        } catch (e) {
          setScanHint(`No match for '${val}'`);
          return;
        }
      }

      if (!target) {
        setScanHint(`No in-stock pallet matches '${val}' — is it in the source warehouse?`);
        return;
      }

      const s = new Set(pickerSelected);
      const already = s.has(target.id);
      s.add(target.id);
      setPickerSelected(s);
      setScanValue('');
      setScanHint(`${already ? 'Already ticked' : 'Ticked'} ${target.pallet_code}`);
      // Move focus to the Load button once at least one pallet is ticked
      setTimeout(() => { loadSelectedBtnRef.current?.focus(); }, 60);
    } finally {
      setScanBusy(false);
    }
  };

  const loadSelectedPallets = async () => {
    if (pickerSelected.size === 0) { toast.error('Select at least one pallet'); return; }
    try {
      // Fetch every selected pallet in parallel
      const details = await Promise.all(
        Array.from(pickerSelected).map((id) => getSerialPallet(id))
      );
      const grouped = {};
      let totalSerials = 0;
      for (const r of details) {
        for (const s of (r.data.serials || [])) {
          const vid = s.variant_id || '';
          if (!vid) continue;
          grouped[vid] = (grouped[vid] || 0) + 1;
          totalSerials += 1;
        }
      }
      const items = Object.entries(grouped).map(([variant_id, quantity]) => ({ variant_id, quantity }));
      if (!items.length) {
        toast.error('Selected pallet(s) have no variant-linked serials to load');
        return;
      }
      setNewTransfer({ ...newTransfer, items });
      setLoadedPalletIds(Array.from(pickerSelected));
      setShowPalletPicker(false);
      toast.success(`Loaded ${totalSerials} serials from ${pickerSelected.size} pallet${pickerSelected.size === 1 ? '' : 's'} → ${items.length} line${items.length === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load pallets');
    }
  };

  const handleShip = async (id) => {
    // Prompt for optional ETA — blank skips it (backwards compatible)
    let eta = null;
    try {
      const raw = window.prompt('Estimated arrival date (YYYY-MM-DD) — leave blank if unknown:');
      if (raw && raw.trim()) {
        // Coerce to end-of-day UTC ISO so the destination sees a specific timestamp
        const d = new Date(raw.trim() + 'T23:59:00');
        if (!isNaN(d.getTime())) eta = d.toISOString();
        else toast.error('Invalid date, shipping without ETA');
      }
    } catch { /* prompt unavailable — ship without ETA */ }
    await shipTransfer(id, eta ? { estimated_arrival: eta } : undefined);
    fetchData();
  };
  const handleReceive = async (id) => { await receiveTransfer(id, {}); fetchData(); };
  const handleStartPick = async (id) => {
    try { await startTransferPick(id); toast.success('Pick list created — check the Operator Console.'); fetchData(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not create pick list'); }
  };
  const handleCreateInboundReceipt = async (id) => {
    try { await createTransferInboundReceipt(id, true); toast.success('Inbound receipt created at destination.'); fetchData(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not create inbound receipt'); }
  };
  const handleCancel = async (id) => {
    if (window.confirm('Cancel this transfer?')) {
      await cancelTransfer(id);
      fetchData();
    }
  };

  // Bulk mark-arrived — creates inbound receipts for every selected
  // in-transit transfer that doesn't already have one. Useful when a
  // full truck of transfers arrives at destination at once.
  const handleBulkMarkArrived = async () => {
    const selected = transferSel.ids
      .map((id) => transfers.find((x) => x.id === id))
      .filter(Boolean);
    const eligible = selected.filter((t) => t.status === 'in_transit' && !t.inbound_receipt_id);
    if (eligible.length === 0) {
      toast.error('Select 1+ in-transit transfer(s) without an inbound receipt yet.');
      return;
    }
    let ok = 0;
    let fail = 0;
    for (const t of eligible) {
      try { await createTransferInboundReceipt(t.id, true); ok += 1; }
      catch { fail += 1; }
    }
    if (ok) toast.success(`Marked ${ok} transfer${ok === 1 ? '' : 's'} arrived — inbound receipts created.`);
    if (fail) toast.error(`${fail} failed — check destination has active locations.`);
    transferSel.clear();
    fetchData();
  };

  // Bulk-settle — only acts when 2+ selected transfers are all
  // status='received', share the same from/to warehouse pair, and none
  // are already settled. Otherwise we surface a helpful toast.
  const handleBulkSettle = () => {
    const selected = transferSel.ids
      .map((id) => transfers.find((x) => x.id === id))
      .filter(Boolean);
    const receivedUnsettled = selected.filter((t) => t.status === 'received' && !t.settlement_invoice_id);
    if (receivedUnsettled.length < 2) {
      toast.error('Select 2+ received transfers that have no settlement yet.');
      return;
    }
    const routes = new Set(receivedUnsettled.map((t) => `${t.from_warehouse_id}:${t.to_warehouse_id}`));
    if (routes.size !== 1) {
      toast.error('All selected transfers must share the same from → to warehouse pair.');
      return;
    }
    setBatchSettleFor(receivedUnsettled);
  };

  // Bulk ship — only acts on the subset whose status is 'pending'.
  const handleBulkShip = async () => {
    const pendingIds = transferSel.ids.filter((id) => {
      const t = transfers.find((x) => x.id === id);
      return t && t.status === 'pending';
    });
    if (pendingIds.length === 0) {
      toast.error('Only pending transfers can be shipped — selection contains none.');
      return;
    }
    if (!window.confirm(`Ship ${pendingIds.length} transfer${pendingIds.length === 1 ? '' : 's'}?`)) return;
    let ok = 0; let failed = 0;
    for (const id of pendingIds) {
      try {
        await shipTransfer(id);
        ok++;
      } catch (e) {
        console.error(e);
        failed++;
      }
    }
    transferSel.clear();
    if (failed > 0) toast.error(`Shipped ${ok}, ${failed} failed`);
    else toast.success(`${ok} transfer${ok === 1 ? '' : 's'} shipped`);
    fetchData();
  };

  // Pending transfers only — header checkbox and per-row checkbox should
  // surface the subset that "Ship" can act on.
  const shippableTransfers = filteredTransfers.filter((t) => t.status === 'pending');

  return (
    <Layout>
      <div data-testid="transfers-page" className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              Warehouse Transfers
              <HelpAnchor articleId="transfers" label="Transfer lifecycle, freight matrix & settlement" />
            </h1>
            <p className="text-muted-foreground">Move stock between warehouses</p>
          </div>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button data-testid="create-transfer-btn" className="bg-brand hover:bg-brand/90">
                <Plus size={18} className="mr-2" /> New Transfer
              </Button>
            </DialogTrigger>
            <DialogContent className="border border-border rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Transfer</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div>
                    <Label className="overline">From Warehouse</Label>
                    <Select value={newTransfer.from_warehouse_id} onValueChange={(v) => setNewTransfer({...newTransfer, from_warehouse_id: v})}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Source" /></SelectTrigger>
                      <SelectContent>
                        {warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.code} - {w.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="overline">To Warehouse</Label>
                    <Select value={newTransfer.to_warehouse_id} onValueChange={(v) => setNewTransfer({...newTransfer, to_warehouse_id: v})}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Destination" /></SelectTrigger>
                      <SelectContent>
                        {warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.code} - {w.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label className="overline">
                      Items
                      {loadedPalletIds.length > 0 && (
                        <span className="ml-2 px-1.5 py-0.5 border border-purple-400 bg-purple-50 text-purple-800 text-[9px] font-black uppercase tracking-widest align-middle" data-testid="transfer-loaded-pallet-badge">
                          Loaded from {loadedPalletIds.length} pallet{loadedPalletIds.length === 1 ? '' : 's'}
                        </span>
                      )}
                    </Label>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={openPalletPicker}
                        data-testid="transfer-open-pallet-picker"
                        title="Load every serial on a pallet into this transfer"
                        className="border-purple-500 text-purple-700 hover:bg-purple-50"
                      >
                        Load whole pallet
                      </Button>
                      <Button type="button" size="sm" variant="outline" onClick={addItem}>
                        <Plus size={14} className="mr-1" /> Add
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {newTransfer.items.map((item, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2">
                        <div className="col-span-7">
                          <Select value={item.variant_id} onValueChange={(v) => updateItem(idx, 'variant_id', v)}>
                            <SelectTrigger><SelectValue placeholder="Select variant" /></SelectTrigger>
                            <SelectContent>
                              {variants.map(v => <SelectItem key={v.id} value={v.id}>{v.barcode} {v.size ? `(${v.size})` : ''}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-3">
                          <Input type="number" min={1} value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', parseInt(e.target.value))} />
                        </div>
                        <div className="col-span-2">
                          <Button type="button" size="sm" variant="outline" onClick={() => removeItem(idx)} className="w-full text-red-600 border-red-200">
                            <X size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="overline">Notes</Label>
                  <Input value={newTransfer.notes} onChange={(e) => setNewTransfer({...newTransfer, notes: e.target.value})} className="mt-1" />
                </div>
                <Button type="submit" className="w-full bg-brand hover:bg-brand/90">Create Transfer</Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Pallet picker — iter206 "Load whole pallet(s)" (multi-select in iter207) */}
          <Dialog open={showPalletPicker} onOpenChange={setShowPalletPicker}>
            <DialogContent className="max-w-2xl" data-testid="transfer-pallet-picker-dialog">
              <DialogHeader>
                <DialogTitle>Load pallets into this transfer</DialogTitle>
              </DialogHeader>
              <div className="text-xs text-muted-foreground">
                Only in-stock pallets{newTransfer.from_warehouse_id ? ' in the source warehouse' : ''} are listed. Tick any number of pallets and click "Load selected" — items merge across pallets grouped by variant.
              </div>

              {/* iter208: scan-to-load — pallet code OR any child serial's QR */}
              <div className="mt-2 flex items-center gap-2 p-2 border border-purple-500 bg-purple-50">
                <Barcode size={16} weight="fill" className="text-purple-700" />
                <Input
                  ref={scanInputRef}
                  autoFocus
                  value={scanValue}
                  onChange={(e) => setScanValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleScan(scanValue); } }}
                  placeholder="Scan pallet QR or any child serial…"
                  className="font-mono text-xs flex-1"
                  disabled={scanBusy}
                  data-testid="transfer-pallet-picker-scan-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="border-purple-500 text-purple-700 hover:bg-purple-100"
                  onClick={() => handleScan(scanValue)}
                  disabled={scanBusy || !scanValue.trim()}
                  data-testid="transfer-pallet-picker-scan-submit"
                >
                  Tick
                </Button>
              </div>
              {scanHint && (
                <div className="text-xs text-purple-800" data-testid="transfer-pallet-picker-scan-hint">
                  {scanHint}
                </div>
              )}
              <div className="max-h-[50vh] overflow-y-auto border border-border mt-2">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-muted-foreground border-b border-border bg-muted/50">
                  <div className="col-span-1">
                    <input
                      type="checkbox"
                      checked={availablePallets.length > 0 && pickerSelected.size === availablePallets.length}
                      onChange={(e) => {
                        if (e.target.checked) setPickerSelected(new Set(availablePallets.map((p) => p.id)));
                        else setPickerSelected(new Set());
                      }}
                      data-testid="transfer-pallet-picker-select-all"
                      title="Select every listed pallet"
                    />
                  </div>
                  <div className="col-span-4">Pallet</div>
                  <div className="col-span-2">Serials</div>
                  <div className="col-span-5">Warehouse</div>
                </div>
                {palletsLoading ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
                ) : availablePallets.length === 0 ? (
                  <div className="p-8 text-center text-sm text-muted-foreground" data-testid="transfer-pallet-picker-empty">
                    No in-stock pallets found. Create one on the Serials page by selecting serials and clicking "Group into pallet".
                  </div>
                ) : availablePallets.map((p) => {
                  const checked = pickerSelected.has(p.id);
                  return (
                    <label
                      key={p.id}
                      className={`grid grid-cols-12 gap-2 px-3 py-2 border-b border-border last:border-b-0 items-center text-sm hover:bg-accent/40 cursor-pointer ${checked ? 'bg-purple-50' : ''}`}
                      data-testid={`transfer-pallet-option-${p.pallet_code}`}
                    >
                      <div className="col-span-1">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const s = new Set(pickerSelected);
                            if (e.target.checked) s.add(p.id); else s.delete(p.id);
                            setPickerSelected(s);
                          }}
                          data-testid={`transfer-pallet-check-${p.pallet_code}`}
                        />
                      </div>
                      <div className="col-span-4 font-mono font-bold">{p.pallet_code}</div>
                      <div className="col-span-2 font-mono">{p.serial_count || 0}</div>
                      <div className="col-span-5 text-xs text-muted-foreground truncate">{p.warehouse_id || '—'}</div>
                    </label>
                  );
                })}
              </div>
              <DialogFooter className="flex-row items-center justify-between gap-3">
                <div className="text-xs text-muted-foreground" data-testid="transfer-pallet-picker-summary">
                  {pickerSelected.size} of {availablePallets.length} selected
                  {pickerSelected.size > 0 && (
                    <> · {availablePallets.filter((p) => pickerSelected.has(p.id)).reduce((n, p) => n + (p.serial_count || 0), 0)} total serials</>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowPalletPicker(false)}>Cancel</Button>
                  <Button
                    ref={loadSelectedBtnRef}
                    onClick={loadSelectedPallets}
                    disabled={pickerSelected.size === 0}
                    data-testid="transfer-pallet-load-selected"
                  >
                    Load selected
                  </Button>
                </div>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <FilterBar
          testId="transfers-filter"
          search={{ value: searchTerm, onChange: setSearchTerm, placeholder: 'Search transfer number…' }}
          filters={[
            {
              key: 'from', value: fromWhFilter, onChange: setFromWhFilter, label: 'From warehouse',
              options: [{ value: '', label: 'All from warehouses' }, ...warehouses.map(w => ({ value: w.id, label: `From: ${w.code}` }))],
            },
            {
              key: 'to', value: toWhFilter, onChange: setToWhFilter, label: 'To warehouse',
              options: [{ value: '', label: 'All to warehouses' }, ...warehouses.map(w => ({ value: w.id, label: `To: ${w.code}` }))],
            },
            {
              key: 'date', value: dateWindow, onChange: setDateWindow, label: 'Date window',
              options: CREATED_WINDOW_OPTIONS,
            },
          ]}
          onClear={clearUrlFilters}
        />

        <div className="flex gap-2 mb-4 flex-wrap">
          <Button variant={filter === '' ? 'default' : 'outline'} onClick={() => setFilter('')} className={filter === '' ? 'bg-brand' : ''}>All</Button>
          {['pending', 'in_transit', 'received', 'cancelled'].map(s => (
            <Button key={s} variant={filter === s ? 'default' : 'outline'} onClick={() => setFilter(s)} className={filter === s ? 'bg-brand' : ''}>
              {s.replace('_', ' ').toUpperCase()}
            </Button>
          ))}
        </div>

        <div className="bg-card border border-border rounded-lg overflow-x-auto hidden md:block">
          <table className="w-full table-dense">
            <thead>
              <tr>
                <th className="w-10">
                  {shippableTransfers.length > 0 && (
                    <Checkbox
                      data-testid="select-all-pending-transfers"
                      aria-label="Select all pending transfers"
                      checked={transferSel.isAllSelected(shippableTransfers)}
                      onCheckedChange={() => transferSel.toggleAll(shippableTransfers)}
                    />
                  )}
                </th>
                <th>Transfer #</th>
                <th>From → To</th>
                <th>Items</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">Loading...</td></tr>
              ) : filteredTransfers.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12">
                  <ArrowsLeftRight size={40} className="mx-auto mb-2 text-slate-300" />
                  <p className="text-muted-foreground">{transfers.length === 0 ? 'No transfers' : 'No transfers match current filters'}</p>
                </td></tr>
              ) : filteredTransfers.map(t => (
                <tr key={t.id} className={transferSel.isSelected(t.id) ? 'bg-blue-50/40' : ''}>
                  <td>
                    {(t.status === 'pending' || (t.status === 'in_transit' && !t.inbound_receipt_id) || (t.status === 'received' && !t.settlement_invoice_id)) && (
                      <Checkbox
                        data-testid={`transfer-checkbox-${t.transfer_number}`}
                        checked={transferSel.isSelected(t.id)}
                        onCheckedChange={() => transferSel.toggle(t.id)}
                        aria-label={`Select transfer ${t.transfer_number}`}
                      />
                    )}
                  </td>
                  <td className="font-mono font-semibold">{t.transfer_number}</td>
                  <td className="text-sm">
                    <span className="font-mono font-bold">{t.from_warehouse_code}</span>
                    <span className="text-muted-foreground mx-2">→</span>
                    <span className="font-mono font-bold">{t.to_warehouse_code}</span>
                  </td>
                  <td className="font-mono">{t.items?.length || 0}</td>
                  <td>
                    <span className={`px-2 py-1 text-xs font-bold uppercase ${statusColors[t.status] || 'bg-muted'}`}>
                      {t.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="text-sm text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</td>
                  <td>
                    <div className="flex gap-1 flex-wrap">
                      {(t.status === 'pending' || t.status === 'picking') && !t.pick_list_id && (
                        <Button size="sm" variant="outline" onClick={() => handleStartPick(t.id)} data-testid={`start-pick-transfer-${t.id}`} title="Create pick list at source warehouse">
                          <HandGrabbing size={14} className="mr-1" /> Start pick
                        </Button>
                      )}
                      {t.status === 'pending' && (
                        <Button size="sm" onClick={() => handleShip(t.id)} className="bg-brand hover:bg-brand/90 text-brand-foreground" data-testid={`ship-transfer-${t.id}`}>
                          <PaperPlaneTilt size={14} className="mr-1" /> Ship
                        </Button>
                      )}
                      {t.status === 'in_transit' && !t.inbound_receipt_id && (
                        <Button size="sm" variant="outline" onClick={() => handleCreateInboundReceipt(t.id)} data-testid={`create-inbound-receipt-${t.id}`} title="Create check-off receipt at destination">
                          <Tray size={14} className="mr-1" /> Mark arrived
                        </Button>
                      )}
                      {t.status === 'in_transit' && (
                        <Button size="sm" onClick={() => handleReceive(t.id)} className="bg-green-600 hover:bg-green-700" data-testid={`receive-transfer-${t.id}`}>
                          <CheckCircle size={14} className="mr-1" /> Receive
                        </Button>
                      )}
                      {(t.status === 'in_transit' || t.status === 'received') && (
                        <Button size="sm" variant="outline" onClick={() => setInvoicingFor(t)} data-testid={`docs-transfer-${t.id}`} title="Documents & invoicing">
                          <Receipt size={14} />
                        </Button>
                      )}
                      {(t.status === 'pending' || t.status === 'picking' || t.status === 'in_transit') && (
                        <Button size="sm" variant="outline" onClick={() => handleCancel(t.id)} className="text-red-600 border-red-200">
                          <X size={14} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile card view */}
        <div className="md:hidden space-y-2" data-testid="transfers-mobile-list">
          {loading ? (
            <p className="text-center py-8 text-muted-foreground text-sm">Loading...</p>
          ) : filteredTransfers.length === 0 ? (
            <div className="border border-dashed border-border rounded-md py-12 text-center">
              <ArrowsLeftRight size={40} className="mx-auto mb-2 text-slate-300" />
              <p className="text-muted-foreground text-sm">{transfers.length === 0 ? 'No transfers' : 'No transfers match current filters'}</p>
            </div>
          ) : filteredTransfers.map(t => (
            <div key={t.id} className={`border ${transferSel.isSelected(t.id) ? 'border-brand bg-blue-50/40' : 'border-border bg-card'} rounded-md p-3`} data-testid={`transfer-mobile-card-${t.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1 flex items-start gap-2">
                  {(t.status === 'pending' || (t.status === 'in_transit' && !t.inbound_receipt_id) || (t.status === 'received' && !t.settlement_invoice_id)) && (
                    <Checkbox
                      data-testid={`transfer-mobile-checkbox-${t.transfer_number}`}
                      checked={transferSel.isSelected(t.id)}
                      onCheckedChange={() => transferSel.toggle(t.id)}
                      className="mt-0.5"
                      aria-label={`Select transfer ${t.transfer_number}`}
                    />
                  )}
                  <div className="min-w-0">
                    <div className="font-mono font-bold text-sm truncate">{t.transfer_number}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      <span className="font-mono font-semibold text-foreground">{t.from_warehouse_code}</span>
                      <span className="mx-1.5">→</span>
                      <span className="font-mono font-semibold text-foreground">{t.to_warehouse_code}</span>
                    </div>
                  </div>
                </div>
                <span className={`shrink-0 px-2 py-1 text-[10px] font-bold uppercase rounded ${statusColors[t.status] || 'bg-muted'}`}>
                  {t.status?.replace('_', ' ')}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground mt-2 pt-2 border-t border-border">
                <span>{t.items?.length || 0} item{(t.items?.length || 0) !== 1 ? 's' : ''} · {new Date(t.created_at).toLocaleDateString()}</span>
                <div className="flex gap-1">
                  {t.status === 'pending' && (
                    <Button size="sm" onClick={() => handleShip(t.id)} className="bg-brand hover:bg-brand/90 text-brand-foreground h-9" data-testid={`ship-transfer-mobile-${t.id}`}>
                      <PaperPlaneTilt size={14} className="mr-1" /> Ship
                    </Button>
                  )}
                  {t.status === 'in_transit' && (
                    <Button size="sm" onClick={() => handleReceive(t.id)} className="bg-green-600 hover:bg-green-700 h-9" data-testid={`receive-transfer-mobile-${t.id}`}>
                      <CheckCircle size={14} className="mr-1" /> Receive
                    </Button>
                  )}
                  {(t.status === 'in_transit' || t.status === 'received') && (
                    <Button size="sm" variant="outline" onClick={() => setInvoicingFor(t)} className="h-9" data-testid={`docs-transfer-mobile-${t.id}`}>
                      <Receipt size={14} />
                    </Button>
                  )}
                  {(t.status === 'pending' || t.status === 'in_transit') && (
                    <Button size="sm" variant="outline" onClick={() => handleCancel(t.id)} className="text-red-600 border-red-200 h-9">
                      <X size={14} />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bulk-actions bar — appears when ≥1 pending transfer is selected. */}
        <BulkActionsBar
          count={transferSel.count}
          onClear={transferSel.clear}
          actions={[
            {
              key: 'ship',
              label: 'Ship',
              icon: <PaperPlaneTilt size={14} weight="fill" />,
              onClick: handleBulkShip,
              testId: 'bulk-ship-transfers-btn',
              title: `Ship ${transferSel.count} pending transfer${transferSel.count === 1 ? '' : 's'}`,
            },
            {
              key: 'mark-arrived',
              label: 'Mark arrived',
              icon: <Tray size={14} weight="fill" />,
              onClick: handleBulkMarkArrived,
              testId: 'bulk-mark-arrived-btn',
              title: 'Create inbound receipts at destinations for all selected in-transit transfers',
            },
            {
              key: 'batch-settle',
              label: 'Batch settle',
              icon: <Receipt size={14} weight="fill" />,
              onClick: handleBulkSettle,
              testId: 'bulk-batch-settle-btn',
              title: 'Consolidate selected same-route received transfers into one invoice',
            },
          ]}
        />

        <TransferInvoicingDialog
          open={!!invoicingFor}
          transfer={invoicingFor}
          onClose={() => setInvoicingFor(null)}
          onChange={fetchData}
        />

        <BatchSettleDialog
          open={!!batchSettleFor}
          transfers={batchSettleFor || []}
          onClose={() => setBatchSettleFor(null)}
          onSettled={() => { transferSel.clear(); fetchData(); }}
        />
      </div>
    </Layout>
  );
}
